/*
  Brief Tabs Controller

  Activates tabs for an activity brief page

  EMITTED EVENTS
    brief-updated (document) Emitted after the brief has been successfully updated so listeners can take action if needed
      detail: {
        el: the root element of this controller instance
      }
*/

import { createCustomEvent } from '../../../../javascript/shared/helpers';

export default class extends Controller {
  static targets = ['initialTabLink', 'content', 'tabItem', 'updateURL', "updateMethod", 'section'];

  connect() {
    if (this.readonly) {
      App.Helpers.onScroll(window, this.setActiveTabFromPosition.bind(this));
    } else {
      this.initialTabLinkTarget && this.getAndSetInitialTabContent();
    }
    App.Helpers.listenToCustomEvent(document, "update-success", this.refreshTab.bind(this))
  }

  goToNewTab(event) {
    event.preventDefault();
    if (this.readonly) {
      document.location = event.target.dataset.url;
      this.repaintTabs(event.target.dataset.tabId);
    } else {
      this.updateAndRenderTabContent(event.target.dataset.tabId);
    }
  }

  advanceTab(event) {
    event && event.preventDefault();
    this.updateAndRenderTabContent(this.nextTab);
  }

  regressTab(event) {
    event && event.preventDefault();
    this.updateAndRenderTabContent(this.prevTab);
  }

  refreshTab(event) {
    event && event.preventDefault()
    this.updateAndRenderTabContent(this.currentTabID)
  }

  submit(event) {
    event && event.preventDefault();
    this.updateAndRenderTabContent(this.currentTabID, true);
  }

  //
  // INTERNAL USE
  //

  getAndSetInitialTabContent() {
    this.content = App.spinner();
    const url = this.initialTabLinkTarget.dataset.url;
    App.Helpers.ajaxRequest({
      url,
      method:         'GET',
      successHandler: (res => this.setContentFromResponse(res, this.currentTabID, true)).bind(this)
    });
  }

  updateAndRenderTabContent(tabID, isFinal=false) {
    const url    = this.updateURL(tabID);
    const data   = App.Helpers.formDataAsString(this.form);
    const method = this.updateMethod;
    this.content = App.spinner();
    const oldTab = this.currentTabID;
    this.repaintTabs(tabID);
    App.Helpers.ajaxRequest({
      url,
      data,
      method,
      successHandler: isFinal ? this.completeBrief.bind(this) : (res => this.setContentFromResponse(res, tabID)),
      errorHandler:   ({ responseText }) => {
        this.content = responseText;
        this.repaintTabs(oldTab);
      }
    });
  }

  setContentFromResponse(res, tabID, isInitialTabLoad=false) {
    document.dispatchEvent(createCustomEvent('brief-updated', this.element));
    const url = App.Helpers.addParamsToURL(location.href, { tab: tabID });
    window.history[isInitialTabLoad ? 'pushState' : 'replaceState'](null, null, url);
    this.content = res;
  }

  completeBrief() {
    document.location = this.afterCompleteURL;
  }

  updateURL(newTab) {
    return this.updateURLTarget.value.replace(/TAB-ID/g, newTab);
  }

  repaintTabs(newTab) {
    this.tabItemTargets.forEach(item => item.classList[item.querySelector('a').dataset.tabId == newTab ? 'add' : 'remove']('active'));
  }

  setActiveTabFromPosition() {
    this.repaintTabs(this.getActiveTabFromPosition());
  }

  getActiveTabFromPosition() {
    return this.sections.reduce((active, el) => el.getBoundingClientRect().top <= 80 ? el : active, this.topSection).id;
  }

  //
  // GETTERS AND SETTERS
  //

  set content(html) {
    const insertedContent = App.Helpers.setContentFromString(this.contentTarget, html, null);
    App.Helpers.addModalHandlers(insertedContent);
  }

  get currentTabID() {
    return this.tabItemTargets.find(tabItem => tabItem.classList.contains('active')).querySelector('a').dataset.tabId;
  }

  get tabIDs() {
    return this.tabItemTargets.map(tabItem => tabItem.querySelector('a').dataset.tabId);
  }

  get nextTab() {
    return this.tabIDs[this.tabIDs.indexOf(this.currentTabID) + 1];
  }

  get prevTab() {
    return this.tabIDs[this.tabIDs.indexOf(this.currentTabID) - 1];
  }

  get form() {
    return this.contentTarget.querySelector('form');
  }

  get afterCompleteURL() {
    return this.data.get('after-complete-url');
  }

  get readonly() {
    return App.Helpers.truthyValues.includes(this.data.get('is-readonly'))
  }

  get sections() {
    return this.sectionTargets || [];
  }

  get topSection() {
    return this.sections[0];
  }

  get updateMethod() {
    return this.hasUpdateMethodTarget ? this.updateMethodTarget.value : "PUT"
  }
};



// WEBPACK FOOTER //
// ./app/components/briefs/assets/javascript/brief-tabs-controller.js